import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AlertTriangle,
    CheckCircle2,
    File as FileIcon,
    Package,
    ShoppingCart,
    Truck,
    Upload,
    X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobilePurchaseImport from '../../component/purchase/MobilePurchaseImport';

const MAX_FILES = 30;
const MAX_SIZE_MB = 10;
const ACCEPT = '.xml,.pdf';

const asList = (data) => (Array.isArray(data) ? data : []);

const errorText = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data.error === 'string') return data.error;
    return fallback;
};

const supplierKey = (supplier) => supplier?.document || `nome:${supplier?.legal_name || supplier?.trade_name || ''}`;

const supplierLabel = (supplier) => supplier?.trade_name || supplier?.legal_name || 'Fornecedor sem nome';

const formatDocument = (digits) => {
    const d = String(digits || '');
    if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return d;
};

const formatDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const thSx = {
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    color: 'text.disabled',
    fontWeight: 700,
    backgroundColor: 'background.subtle',
    borderBottom: '1px solid',
    borderColor: 'divider',
    whiteSpace: 'nowrap',
};

const tdSx = {
    fontSize: '12.5px',
    color: 'text.secondary',
    borderBottom: '1px solid',
    borderColor: 'divider',
    verticalAlign: 'middle',
};

const cardSx = {
    borderRadius: (t) => `${t.customRadius.card}px`,
    boxShadow: (t) => t.customShadows.sm,
};

// Item que ainda pode receber ação (não lançado nesta sessão e sem entrada anterior para a nota).
const isSelectable = (item) => !item.done && !item.already_imported && item.quantity > 0;

export default function Import() {
    const fileInputRef = useRef(null);
    const isMobileShell = useIsMobileShell();

    const [files, setFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [updateCost, setUpdateCost] = useState(false);
    const [dueDate, setDueDate] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    // Decisão do usuário sobre cadastrar fornecedor não encontrado: { [supplierKey]: boolean }
    const [supplierDecision, setSupplierDecision] = useState({});
    const [supplierPrompt, setSupplierPrompt] = useState(null); // { action, createOpts, entries: [{ key, label, document, checked }] }
    const [createDialog, setCreateDialog] = useState(false);
    const [createOpts, setCreateOpts] = useState({ category: null, subcategory: null, markup: '0', minQuantity: '0' });

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    useEffect(() => {
        httpClient.get('/stock/all').then((r) => setProducts(asList(r.data))).catch(() => setProducts([]));
        httpClient.get('/stock/categories').then((r) => setCategories(asList(r.data))).catch(() => setCategories([]));
    }, []);

    useEffect(() => {
        if (!createOpts.category) {
            setSubcategories([]);
            return;
        }
        httpClient
            .get('/stock/subcategories', { params: { categoryId: createOpts.category.id } })
            .then((r) => setSubcategories(asList(r.data)))
            .catch(() => setSubcategories([]));
    }, [createOpts.category]);

    // ---------- seleção de arquivos ----------
    const addFiles = (list) => {
        const incoming = Array.from(list || []);
        if (!incoming.length) return;

        const accepted = [];
        const rejected = [];
        incoming.forEach((f) => {
            const name = f.name.toLowerCase();
            if (!name.endsWith('.xml') && !name.endsWith('.pdf')) rejected.push(`${f.name} (formato)`);
            else if (f.size > MAX_SIZE_MB * 1024 * 1024) rejected.push(`${f.name} (acima de ${MAX_SIZE_MB}MB)`);
            else accepted.push(f);
        });

        setFiles((current) => {
            const known = new Set(current.map((f) => `${f.name}:${f.size}`));
            const merged = [...current, ...accepted.filter((f) => !known.has(`${f.name}:${f.size}`))];
            if (merged.length > MAX_FILES) {
                showSnackbar(`Selecione no máximo ${MAX_FILES} arquivos por importação.`, 'error');
                return merged.slice(0, MAX_FILES);
            }
            return merged;
        });
        if (rejected.length) showSnackbar(`Arquivos ignorados: ${rejected.join(', ')}.`, 'error');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => setFiles((current) => current.filter((_, i) => i !== index));

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        addFiles(e.dataTransfer.files);
    };

    // ---------- leitura das notas ----------
    const handleParse = async () => {
        if (!files.length || parsing) return;
        setParsing(true);
        const formData = new FormData();
        files.forEach((f) => formData.append('files', f));

        try {
            const response = await httpClient.post('/purchases/parse', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const parsed = asList(response.data?.invoices).map((inv, i) => ({
                ...inv,
                uid: `${Date.now()}-${i}`,
                items: asList(inv.items).map((it) => ({
                    ...it,
                    key: `${i}-${it.index}`,
                    selected: false,
                    done: false,
                    result: '',
                    linked: it.product_id ? { id: it.product_id, code: it.product_code, name: it.product_name } : null,
                })),
                result: null,
            }));
            setInvoices(parsed);
            setFiles([]);
            const failed = parsed.filter((inv) => inv.error).length;
            if (failed) showSnackbar(`${failed} arquivo(s) não puderam ser lidos.`, 'error');
        } catch (error) {
            showSnackbar(errorText(error, 'Erro ao ler os arquivos.'), 'error');
        } finally {
            setParsing(false);
        }
    };

    // ---------- seleção / vínculo de itens ----------
    const updateItem = (invoiceUid, itemKey, patch) => {
        setInvoices((current) => current.map((inv) => (inv.uid !== invoiceUid ? inv : {
            ...inv,
            items: inv.items.map((it) => (it.key !== itemKey ? it : { ...it, ...patch })),
        })));
    };

    const toggleInvoice = (invoiceUid, checked) => {
        setInvoices((current) => current.map((inv) => (inv.uid !== invoiceUid ? inv : {
            ...inv,
            items: inv.items.map((it) => (isSelectable(it) ? { ...it, selected: checked } : it)),
        })));
    };

    const toggleAll = (checked) => {
        setInvoices((current) => current.map((inv) => ({
            ...inv,
            items: inv.items.map((it) => (isSelectable(it) ? { ...it, selected: checked } : it)),
        })));
    };

    const selectedItems = useMemo(
        () => invoices.flatMap((inv) => inv.items.filter((it) => it.selected && isSelectable(it))),
        [invoices],
    );
    const selectableCount = useMemo(
        () => invoices.reduce((total, inv) => total + inv.items.filter(isSelectable).length, 0),
        [invoices],
    );
    const updatable = selectedItems.filter((it) => it.linked);
    const creatable = selectedItems.filter((it) => !it.linked);

    // ---------- aplicação ----------
    const eligible = (item, action) => (action === 'update' ? Boolean(item.linked) : !item.linked);

    const startApply = (action, opts) => {
        const involved = invoices.filter((inv) => inv.items.some((it) => it.selected && isSelectable(it) && eligible(it, action)));
        const seen = new Set();
        const unknown = [];
        involved.forEach((inv) => {
            const key = supplierKey(inv.supplier);
            if (inv.supplier_exists || supplierDecision[key] !== undefined || seen.has(key)) return;
            seen.add(key);
            unknown.push({ key, label: supplierLabel(inv.supplier), document: inv.supplier.document, checked: true });
        });

        if (unknown.length) {
            setSupplierPrompt({ action, createOpts: opts, entries: unknown });
            return;
        }
        submit(action, opts, supplierDecision);
    };

    const handleUpdateClick = () => {
        if (!updatable.length) {
            showSnackbar('Selecione itens vinculados a um produto cadastrado para atualizar o estoque.', 'error');
            return;
        }
        if (creatable.length) {
            showSnackbar(`${creatable.length} item(ns) sem produto vinculado foram ignorados nesta ação.`, 'info');
        }
        startApply('update', null);
    };

    const handleCreateClick = () => {
        if (!creatable.length) {
            showSnackbar('Selecione itens sem produto vinculado para criar novos produtos.', 'error');
            return;
        }
        if (updatable.length) {
            showSnackbar(`${updatable.length} item(ns) já vinculados a produto foram ignorados nesta ação.`, 'info');
        }
        setCreateDialog(true);
    };

    const handleConfirmCreate = () => {
        if (!createOpts.category) return;
        setCreateDialog(false);
        startApply('create', createOpts);
    };

    const handleSupplierConfirm = () => {
        const decision = { ...supplierDecision };
        supplierPrompt.entries.forEach((e) => { decision[e.key] = e.checked; });
        setSupplierDecision(decision);
        const { action, createOpts: opts } = supplierPrompt;
        setSupplierPrompt(null);
        submit(action, opts, decision);
    };

    const submit = async (action, opts, decision) => {
        const payloadInvoices = [];
        const payloadRefs = [];

        invoices.forEach((inv) => {
            const items = inv.items
                .filter((it) => it.selected && isSelectable(it) && eligible(it, action))
                .map((it) => {
                    const base = { index: it.index, action, quantity: it.quantity, unit_cost: it.unit_cost };
                    if (action === 'update') return { ...base, product_id: it.linked.id };
                    return {
                        ...base,
                        code: it.code,
                        name: it.name,
                        description: '',
                        category_id: opts.category.id,
                        subcategory_id: opts.subcategory?.id || '',
                        markup_percent: Number(String(opts.markup).replace(',', '.')) || 0,
                        min_quantity: parseInt(opts.minQuantity, 10) || 0,
                    };
                });
            if (!items.length) return;

            payloadInvoices.push({
                file_name: inv.file_name,
                access_key: inv.access_key,
                number: inv.number,
                series: inv.series,
                issue_date: inv.issue_date,
                total: inv.total || '',
                due_date: dueDate,
                supplier_id: inv.supplier_id || '',
                create_supplier: !inv.supplier_exists && decision[supplierKey(inv.supplier)] === true,
                supplier: inv.supplier,
                items,
            });
            payloadRefs.push(inv.uid);
        });

        if (!payloadInvoices.length) return;

        setSubmitting(true);
        try {
            const response = await httpClient.post('/purchases/confirm', { update_cost: updateCost, invoices: payloadInvoices });
            const results = asList(response.data?.invoices);

            setInvoices((current) => current.map((inv) => {
                const pos = payloadRefs.indexOf(inv.uid);
                const res = pos >= 0 ? results[pos] : null;
                if (!res) return inv;

                const byIndex = new Map(asList(res.items).map((r) => [r.index, r]));
                return {
                    ...inv,
                    result: res,
                    supplier_exists: inv.supplier_exists || Boolean(res.supplier_id),
                    supplier_id: res.supplier_id || inv.supplier_id,
                    items: inv.items.map((it) => {
                        const r = byIndex.get(it.index);
                        if (!r) return it;
                        if (r.status === 'ok') {
                            return {
                                ...it,
                                selected: false,
                                done: true,
                                result: action === 'create' ? 'Produto criado e estoque lançado' : 'Estoque atualizado',
                                linked: it.linked || { id: r.product_id, code: it.code, name: it.name },
                            };
                        }
                        return { ...it, selected: false, result: r.message || 'Não lançado' };
                    }),
                };
            }));

            const ok = results.reduce((t, r) => t + (r.created || 0) + (r.updated || 0), 0);
            const total = payloadInvoices.reduce((t, inv) => t + inv.items.length, 0);
            if (ok === total) showSnackbar(`${ok} item(ns) lançado(s) com sucesso.`);
            else showSnackbar(`${ok} de ${total} item(ns) lançados. Veja os detalhes na lista.`, ok ? 'warning' : 'error');
        } catch (error) {
            showSnackbar(errorText(error, 'Erro ao lançar a compra.'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCategoryChange = (category) => {
        setCreateOpts((o) => ({
            ...o,
            category,
            subcategory: null,
            markup: category && category.profit_margin ? String(category.profit_margin) : o.markup,
        }));
    };

    const hasInvoices = invoices.length > 0;

    const dialogs = (
        <>
            {/* ============ DIALOG: CRIAR PRODUTOS ============ */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Criar produtos a partir da nota</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {creatable.length} produto(s) serão criados com o código, o nome, o custo e a quantidade da nota. Informe a categoria e a margem para formar o preço de venda.
                    </Typography>
                    <Autocomplete
                        options={categories}
                        value={createOpts.category}
                        getOptionLabel={(o) => o.name || ''}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, value) => handleCategoryChange(value)}
                        renderInput={(params) => <TextField {...params} label="Categoria" required sx={standaloneFieldSx} />}
                    />
                    <Autocomplete
                        options={subcategories}
                        value={createOpts.subcategory}
                        disabled={!createOpts.category}
                        getOptionLabel={(o) => o.name || ''}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, value) => setCreateOpts((o) => ({ ...o, subcategory: value }))}
                        renderInput={(params) => <TextField {...params} label="Subcategoria (opcional)" sx={standaloneFieldSx} />}
                    />
                    <TextField
                        label="Margem sobre o custo (%)"
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                        value={createOpts.markup}
                        onChange={(e) => setCreateOpts((o) => ({ ...o, markup: e.target.value }))}
                        helperText="Preço de venda = custo da nota + margem. Pode ser ajustado depois no cadastro do produto."
                        sx={standaloneFieldSx}
                    />
                    <TextField
                        label="Estoque mínimo"
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        value={createOpts.minQuantity}
                        onChange={(e) => setCreateOpts((o) => ({ ...o, minQuantity: e.target.value }))}
                        sx={standaloneFieldSx}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={() => setCreateDialog(false)}>Cancelar</Button>
                    <Button variant="contained" disabled={!createOpts.category} onClick={handleConfirmCreate}>
                        Criar e dar entrada
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============ DIALOG: FORNECEDOR NÃO CADASTRADO ============ */}
            <Dialog open={Boolean(supplierPrompt)} onClose={() => setSupplierPrompt(null)} fullWidth maxWidth="sm">
                <DialogTitle>Fornecedor não cadastrado</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
                        O fornecedor da nota ainda não existe no sistema. Deseja cadastrá-lo com os dados da nota? Os produtos criados ficarão vinculados a ele.
                    </Typography>
                    {supplierPrompt?.entries.map((entry, i) => (
                        <FormControlLabel
                            key={entry.key}
                            sx={{ display: 'flex', ml: 0 }}
                            control={(
                                <Checkbox
                                    checked={entry.checked}
                                    onChange={(e) => setSupplierPrompt((p) => ({
                                        ...p,
                                        entries: p.entries.map((x, j) => (j === i ? { ...x, checked: e.target.checked } : x)),
                                    }))}
                                />
                            )}
                            label={`Cadastrar ${entry.label}${entry.document ? ` (${formatDocument(entry.document)})` : ''}`}
                        />
                    ))}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={() => setSupplierPrompt(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSupplierConfirm}>Continuar</Button>
                </DialogActions>
            </Dialog>
        </>
    );

    // Shell mobile: protótipo `compras/importar.html` (mesma leitura, vínculo e lançamento).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[{ title: 'Compras', link: '/purchases' }]}
                titlePageTarget="Importar NF-e"
                openSnackbar={snackbar.open}
                setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
                message={snackbar.message}
                severity={snackbar.severity}
            >
                <MobilePurchaseImport
                    fileInputRef={fileInputRef}
                    accept={ACCEPT}
                    maxFiles={MAX_FILES}
                    maxSizeMb={MAX_SIZE_MB}
                    files={files}
                    parsing={parsing}
                    onAddFiles={addFiles}
                    onRemoveFile={removeFile}
                    onParse={handleParse}
                    invoices={invoices}
                    products={products}
                    updateCost={updateCost}
                    onUpdateCost={setUpdateCost}
                    dueDate={dueDate}
                    onDueDate={setDueDate}
                    isSelectable={isSelectable}
                    selectedCount={selectedItems.length}
                    selectableCount={selectableCount}
                    creatableCount={creatable.length}
                    updatableCount={updatable.length}
                    submitting={submitting}
                    onToggleAll={toggleAll}
                    onToggleInvoice={toggleInvoice}
                    onUpdateItem={updateItem}
                    onCreate={handleCreateClick}
                    onUpdate={handleUpdateClick}
                    supplierLabel={supplierLabel}
                    formatDocument={formatDocument}
                />
                {dialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[{ title: 'Compras', link: '/purchases' }]}
            titlePageTarget="Importar NF-e"
            openSnackbar={snackbar.open}
            setOpenSnackbar={(open) => setSnackbar((s) => ({ ...s, open }))}
            message={snackbar.message}
            severity={snackbar.severity}
        >
            <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', background: (t) => t.palette.gradient.brand,
                    }}>
                        <ShoppingCart size={18} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Compras
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Importe notas fiscais de compra (XML ou PDF) para dar entrada no estoque e cadastrar produtos.
                        </Typography>
                    </Box>
                </Box>

                {/* ============ UPLOAD ============ */}
                <Card sx={cardSx}>
                    <CardContent sx={{ p: { xs: 2, sm: 2.75 } }}>
                        <Typography sx={{ fontSize: '14.5px', fontWeight: 700, mb: '4px' }}>Notas fiscais</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 2 }}>
                            Selecione um ou vários arquivos (.xml ou .pdf, até {MAX_SIZE_MB}MB cada, no máximo {MAX_FILES}). O XML da NF-e é lido
                            com exatidão; a leitura do PDF usa inteligência artificial e deve ser conferida.
                        </Typography>

                        <Box
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                border: '2px dashed',
                                borderColor: dragActive ? 'primary.main' : 'divider',
                                borderRadius: '16px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: dragActive ? 'info.bg' : 'background.subtle',
                                p: '28px 20px',
                                transition: 'all .15s ease',
                                '&:hover': { borderColor: 'primary.main' },
                            }}
                        >
                            <Box sx={{ color: 'primary.main', mb: 1 }}><Upload size={24} strokeWidth={2} /></Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: '4px' }}>Arraste as notas aqui</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>ou clique para selecionar do seu computador</Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPT}
                                multiple
                                hidden
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => addFiles(e.target.files)}
                            />
                        </Box>

                        {files.length > 0 && (
                            <Stack spacing={1} sx={{ mt: 2 }}>
                                {files.map((f, i) => (
                                    <Box key={`${f.name}:${f.size}`} sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 12px',
                                        border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.subtle',
                                    }}>
                                        <Box sx={{ color: 'primary.main', display: 'flex' }}><FileIcon size={18} strokeWidth={2} /></Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{f.name}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{(f.size / 1024).toFixed(1)} KB</Typography>
                                        {!parsing && (
                                            <Tooltip title="Remover arquivo">
                                                <IconButton size="small" onClick={() => removeFile(i)}><X size={14} /></IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mt: 2.25 }}>
                            <Button
                                variant="contained"
                                disabled={!files.length || parsing}
                                startIcon={parsing ? <CircularProgress size={14} thickness={5} color="inherit" /> : <Upload size={15} strokeWidth={2} />}
                                onClick={handleParse}
                            >
                                {parsing ? 'Lendo notas...' : `Ler notas (${files.length})`}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* ============ ITENS ============ */}
                {hasInvoices && (
                    <>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>
                                        {selectedItems.length} de {selectableCount} item(ns) selecionado(s)
                                    </Typography>
                                    <FormControlLabel
                                        sx={{ ml: 0, mt: 0.5 }}
                                        control={<Switch size="small" checked={updateCost} onChange={(e) => setUpdateCost(e.target.checked)} />}
                                        label={<Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Atualizar o custo dos produtos existentes com o valor da nota</Typography>}
                                    />
                                </Box>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                    <DatePicker
                                        label="Vencimento"
                                        format="DD/MM/YYYY"
                                        value={dueDate ? dayjs(dueDate) : null}
                                        onChange={(v) => setDueDate(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                                        slotProps={{
                                            textField: {
                                                helperText: 'Conta a pagar gerada para cada nota',
                                                sx: {
                                                    ...standaloneFieldSx,
                                                    '& .MuiPickersOutlinedInput-root': {
                                                        height: '52px',
                                                        borderRadius: '12px',
                                                        backgroundColor: 'background.subtle',
                                                    },
                                                    '& .MuiPickersInputBase-sectionsContainer': {
                                                        fontSize: '13.5px',
                                                        padding: '21px 0 7px 0',
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                                    <Button variant="outlined" disabled={!selectableCount} onClick={() => toggleAll(selectedItems.length !== selectableCount)}>
                                        {selectedItems.length === selectableCount && selectableCount > 0 ? 'Limpar seleção' : 'Selecionar todos'}
                                    </Button>
                                    <Button variant="outlined" disabled={submitting || !selectedItems.length} startIcon={<Package size={15} strokeWidth={2} />} onClick={handleCreateClick}>
                                        Criar produto ({creatable.length})
                                    </Button>
                                    <Button variant="contained" disabled={submitting || !selectedItems.length} onClick={handleUpdateClick}>
                                        {submitting ? 'Lançando...' : `Atualizar estoque (${updatable.length})`}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>

                        {invoices.map((inv) => {
                            if (inv.error) {
                                return (
                                    <Alert key={inv.uid} severity="error" icon={<AlertTriangle size={16} strokeWidth={2} />}>
                                        <b>{inv.file_name}</b>: {inv.error}
                                    </Alert>
                                );
                            }
                            const selectable = inv.items.filter(isSelectable);
                            const selectedCount = selectable.filter((it) => it.selected).length;
                            return (
                                <Card key={inv.uid} sx={cardSx}>
                                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>
                                                    NF-e {inv.number || '—'}{inv.series ? ` / série ${inv.series}` : ''}
                                                    <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.disabled', ml: 1 }}>
                                                        {inv.file_name} ({inv.source === 'pdf' ? 'PDF' : 'XML'})
                                                    </Box>
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                                                    <Truck size={14} strokeWidth={2} />
                                                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                                                        {supplierLabel(inv.supplier)}
                                                        {inv.supplier.document ? ` — ${formatDocument(inv.supplier.document)}` : ''}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={inv.supplier_exists ? 'Fornecedor cadastrado' : 'Fornecedor não cadastrado'}
                                                        color={inv.supplier_exists ? 'success' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Emissão {formatDate(inv.issue_date)}</Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{inv.total ? `R$ ${inv.total}` : '—'}</Typography>
                                            </Box>
                                        </Box>

                                        {inv.source === 'pdf' && (
                                            <Alert severity="info" sx={{ mb: 1.5 }}>
                                                Dados extraídos do PDF por inteligência artificial: confira códigos, quantidades e valores antes de lançar.
                                            </Alert>
                                        )}
                                        {inv.result && (
                                            <Alert
                                                severity={inv.result.status === 'ok' ? 'success' : (inv.result.status === 'partial' ? 'warning' : 'error')}
                                                icon={inv.result.status === 'ok' ? <CheckCircle2 size={16} strokeWidth={2} /> : undefined}
                                                sx={{ mb: 1.5 }}
                                            >
                                                {inv.result.message
                                                    || `${inv.result.updated} produto(s) atualizado(s), ${inv.result.created} criado(s)${inv.result.supplier_created ? ', fornecedor cadastrado' : ''}.`}
                                            </Alert>
                                        )}

                                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={thSx} padding="checkbox">
                                                            <Checkbox
                                                                size="small"
                                                                disabled={!selectable.length}
                                                                checked={selectable.length > 0 && selectedCount === selectable.length}
                                                                indeterminate={selectedCount > 0 && selectedCount < selectable.length}
                                                                onChange={(e) => toggleInvoice(inv.uid, e.target.checked)}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={thSx}>Código</TableCell>
                                                        <TableCell sx={thSx}>Descrição</TableCell>
                                                        <TableCell sx={thSx} align="right">Qtd</TableCell>
                                                        <TableCell sx={thSx} align="right">Custo unit.</TableCell>
                                                        <TableCell sx={thSx} align="right">Total</TableCell>
                                                        <TableCell sx={{ ...thSx, minWidth: 260 }}>Produto no sistema</TableCell>
                                                        <TableCell sx={thSx}>Situação</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {inv.items.map((it) => {
                                                        const locked = it.done || it.already_imported;
                                                        return (
                                                            <TableRow key={it.key} hover selected={it.selected} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                                                                <TableCell sx={tdSx} padding="checkbox">
                                                                    <Checkbox
                                                                        size="small"
                                                                        disabled={!isSelectable(it)}
                                                                        checked={it.selected}
                                                                        onChange={(e) => updateItem(inv.uid, it.key, { selected: e.target.checked })}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ ...tdSx, fontWeight: 700, color: 'text.primary' }}>{it.code || '—'}</TableCell>
                                                                <TableCell sx={tdSx}>
                                                                    {it.name}
                                                                    {it.warning && (
                                                                        <Typography sx={{ fontSize: 11, color: 'warning.main' }}>{it.warning}</Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={tdSx} align="right">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</TableCell>
                                                                <TableCell sx={tdSx} align="right">{it.unit_cost}</TableCell>
                                                                <TableCell sx={tdSx} align="right">{it.total}</TableCell>
                                                                <TableCell sx={tdSx}>
                                                                    <Autocomplete
                                                                        size="small"
                                                                        disabled={locked}
                                                                        options={products}
                                                                        value={it.linked}
                                                                        getOptionLabel={(o) => (o.code ? `${o.code} - ${o.name}` : o.name || '')}
                                                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                                                        onChange={(_, value) => updateItem(inv.uid, it.key, { linked: value ? { id: value.id, code: value.code, name: value.name } : null })}
                                                                        noOptionsText="Nenhum produto encontrado"
                                                                        renderInput={(params) => <TextField {...params} placeholder="Novo produto (sem vínculo)" />}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={tdSx}>
                                                                    {it.done && <Chip size="small" color="success" label={it.result || 'Lançado'} />}
                                                                    {!it.done && it.already_imported && <Chip size="small" label="Já lançado nesta nota" />}
                                                                    {!it.done && !it.already_imported && it.result && (
                                                                        <Tooltip title={it.result}>
                                                                            <Chip size="small" color="error" variant="outlined" label="Não lançado" />
                                                                        </Tooltip>
                                                                    )}
                                                                    {!it.done && !it.already_imported && !it.result && (
                                                                        <Chip size="small" variant="outlined" color={it.linked ? 'info' : 'default'} label={it.linked ? 'Atualiza estoque' : 'Criar produto'} />
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </>
                )}
            </Box>

            {dialogs}
        </PageScaffold>
    );
}
